import { API_ROOT } from '../config';

export type ApiOptions = Readonly<{
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  jwt?: string | null;
  jsonBody?: Record<string, unknown>;
  body?: string | FormData | File | null | undefined;
  uploadFile?: File;
  headers?: Record<string, string>;
}>;

// Loosely based on axios schema
export type ApiResponse<T> = Readonly<{
  status: number;
  headers: Headers;
  data: T;
}>;

export class ApiError extends Error {
  response: ApiResponse<unknown>;
  constructor(message: string, response: ApiResponse<unknown>) {
    super(message);
    this.response = response;
  }
}

/**
 * The message an API Platform error carries, preferring its `hydra:description`
 * / `detail` over the transport error's own text, or a plain fallback.
 */
export function getApiErrorMessage(err: unknown): string {
  const data = (err instanceof ApiError ? err.response?.data : {}) as Record<
    string,
    unknown
  >;
  const pick = (key: string) =>
    typeof data[key] === 'string' ? (data[key] as string) : null;
  return (
    pick('hydra:description') ??
    pick('detail') ??
    pick('hydra:title') ??
    'Something went wrong. Please try again.'
  );
}

/**
 * Given a path + options, resolve to an ApiResponse, or throw an ApiError
 */
export default async function hubApiRequest<T>(
  path: string,
  options: ApiOptions
): Promise<ApiResponse<T>> {
  const { method = 'GET', jwt, jsonBody, uploadFile } = options;

  if (uploadFile && (path !== '/uploads' || method !== 'POST')) {
    throw new Error('api should only be passed uploadFile on "POST /uploads"');
  }

  let body: string | FormData | File | null | undefined = options.body;
  if (jsonBody) body = JSON.stringify(jsonBody);
  if (uploadFile) {
    body = new FormData();
    body.append('file', uploadFile);
  }

  const strippedPath = path.replace(/^\//, '');
  const fetchResponse = await fetch(`${API_ROOT}/${strippedPath}`, {
    method,
    headers: {
      Accept: 'application/ld+json',
      ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
      'Cache-Control': 'no-cache',
      ...(jsonBody
        ? {
            'Content-Type':
              method === 'PATCH'
                ? 'application/merge-patch+json'
                : 'application/json',
          }
        : {}),
      ...(options.headers || {}),
    },
    body,
    // Useful for analytics, give cookies a headstart to be set before the
    // delayed analytics request fires.
    credentials: 'include',
  });

  const contentTypes = fetchResponse.headers.get('content-type') || '';
  if (
    !contentTypes.includes('application/json') &&
    !contentTypes.includes('application/ld+json') &&
    fetchResponse.status !== 204 // empty response might have misleading content type
  ) {
    const text = await fetchResponse.text();
    throw new ApiError(
      `${method} ${path} failed (${fetchResponse.status}); no json`,
      {
        status: fetchResponse.status,
        headers: fetchResponse.headers,
        data: text,
      }
    );
  }

  const json: T =
    fetchResponse.status === 204 ? ({} as T) : await fetchResponse.json();

  const apiResponse: ApiResponse<T> = {
    data: json,
    status: fetchResponse.status,
    headers: fetchResponse.headers,
  };

  if (!fetchResponse.ok) {
    throw new ApiError(
      `${method} ${path} failed (${fetchResponse.status})`,
      apiResponse
    );
  }

  return apiResponse;
}
